// WebSocket service for real-time updates
class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Map();
    this.subscriptions = new Set();
    this.isConnected = false;
    this.token = null;
  }

  connect(token = null) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.token = token;
    
    // Use ws:// for localhost, wss:// for production
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    
    // On production domain, always use same-origin WebSocket
    const isProduction = host.includes('kahveligi.com');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let wsUrl;
    
    if (isProduction) {
      // Production - use same domain for WebSocket
      wsUrl = `${protocol}//${host}/api/ws`;
    } else if (backendUrl.includes('preview.emergentagent.com')) {
      // Preview - use the backend URL
      wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';
    } else {
      // Local development
      wsUrl = `${protocol}//${host}/api/ws`;
    }

    try {
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Authenticate if token available
        if (this.token) {
          this.send({ action: 'authenticate', token: this.token });
        }
        
        // Resubscribe to previous subscriptions
        this.subscriptions.forEach(sub => {
          const [type, id] = sub.split(':');
          if (type === 'league') {
            this.send({ action: 'subscribe_league', league_id: id });
          } else if (type === 'tournament') {
            this.send({ action: 'subscribe_tournament', tournament_id: id });
          }
        });
        
        this.emit('connected', {});
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.isConnected = false;
        this.emit('disconnected', {});
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emit('error', error);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.attemptReconnect();
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`Reconnecting... attempt ${this.reconnectAttempts}`);
    
    setTimeout(() => {
      this.connect(this.token);
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.subscriptions.clear();
  }

  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  authenticate(token) {
    this.token = token;
    if (this.isConnected) {
      this.send({ action: 'authenticate', token });
    }
  }

  subscribeToLeague(leagueId) {
    const sub = `league:${leagueId}`;
    this.subscriptions.add(sub);
    if (this.isConnected) {
      this.send({ action: 'subscribe_league', league_id: leagueId });
    }
  }

  subscribeToTournament(tournamentId) {
    const sub = `tournament:${tournamentId}`;
    this.subscriptions.add(sub);
    if (this.isConnected) {
      this.send({ action: 'subscribe_tournament', tournament_id: tournamentId });
    }
  }

  unsubscribe(type, id) {
    this.subscriptions.delete(`${type}:${id}`);
  }

  handleMessage(data) {
    const { type, event, ...rest } = data;
    
    // Emit to specific listeners
    this.emit(type, data);
    
    // Also emit a combined event for easier handling
    if (type === 'league_update') {
      this.emit(`league:${rest.league_id}`, data);
    } else if (type === 'tournament_update') {
      this.emit(`tournament:${rest.tournament_id}`, data);
    }
  }

  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.get(eventType)?.delete(callback);
    };
  }

  off(eventType, callback) {
    this.listeners.get(eventType)?.delete(callback);
  }

  emit(eventType, data) {
    this.listeners.get(eventType)?.forEach(callback => {
      try {
        callback(data);
      } catch (e) {
        console.error('WebSocket listener error:', e);
      }
    });
  }

  // Ping to keep connection alive
  startPing(interval = 30000) {
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.send({ action: 'ping' });
      }
    }, interval);
  }

  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  }
}

// Singleton instance
const wsService = new WebSocketService();

export default wsService;
